import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, TrendingDown } from "lucide-react";

export default function StatsTab({ token }) {
  const { API } = useContext(SiteContext);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analytics/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data)).catch(() => {});
  }, [token, API]);

  const depthLabels = { "25": "25%", "50": "50%", "75": "75%", "100": "100%" };

  return (
    <div className="space-y-6 py-4">
      {stats ? (
        <>
          {/* Visitors */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-ink-800 border border-ink-600 rounded-sm p-4 text-center">
              <p className="font-cinzel text-3xl text-verde">{stats.total_visits}</p>
              <p className="font-manrope text-[9px] tracking-widest uppercase text-[#525252] mt-1">Total Visits</p>
            </div>
            <div className="bg-ink-800 border border-ink-600 rounded-sm p-4 text-center">
              <p className="font-cinzel text-3xl text-verde">{stats.unique_visitors || 0}</p>
              <p className="font-manrope text-[9px] tracking-widest uppercase text-[#525252] mt-1">Unique Visitors</p>
            </div>
            <div className="bg-ink-800 border border-ink-600 rounded-sm p-4 text-center">
              <p className="font-cinzel text-3xl text-verde">{stats.today_visits || 0}</p>
              <p className="font-manrope text-[9px] tracking-widest uppercase text-[#525252] mt-1">Today</p>
            </div>
            <div className="bg-ink-800 border border-ink-600 rounded-sm p-4 text-center">
              <p className="font-cinzel text-3xl text-verde">{stats.contact_submissions}</p>
              <p className="font-manrope text-[9px] tracking-widest uppercase text-[#525252] mt-1">Inquiries</p>
            </div>
          </div>

          {/* Scroll Engagement */}
          <div className="bg-ink-800 border border-ink-600 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown size={14} className="text-verde" />
              <p className="font-manrope text-[10px] tracking-widest uppercase text-verde">Scroll Engagement</p>
            </div>

            {stats.scroll_sessions > 0 ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-manrope text-xs text-[#A3A3A3]">{stats.scroll_sessions} tracked sessions</p>
                  {stats.avg_time_on_page > 0 && (
                    <p className="font-manrope text-xs text-[#525252]">
                      Avg. time on page: <span className="text-verde">{Math.floor(stats.avg_time_on_page / 60)}m {Math.round(stats.avg_time_on_page % 60)}s</span>
                    </p>
                  )}
                </div>

                {/* Depth funnel */}
                <div className="space-y-2 mb-4">
                  {["25", "50", "75", "100"].map(depth => {
                    const count = stats.scroll_depths?.[depth] || 0;
                    const pct = stats.scroll_sessions > 0 ? Math.round((count / stats.scroll_sessions) * 100) : 0;
                    return (
                      <div key={depth} className="flex items-center gap-3">
                        <span className="font-manrope text-[10px] text-[#525252] w-10 text-right">{depthLabels[depth]}</span>
                        <div className="flex-1 bg-ink-900 rounded-full h-3 overflow-hidden">
                          <div
                            className="h-full bg-verde/60 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="font-manrope text-[10px] text-[#A3A3A3] w-16 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>

                {/* Section reaches */}
                {stats.section_reaches?.length > 0 && (
                  <div>
                    <p className="font-manrope text-[9px] tracking-widest uppercase text-[#525252] mb-2">Sections Reached</p>
                    <div className="flex flex-wrap gap-2">
                      {stats.section_reaches.map(s => (
                        <span key={s.section} className="font-manrope text-[10px] bg-ink-900 border border-ink-600 px-3 py-1.5 rounded-sm text-[#A3A3A3]">
                          {s.section} <span className="text-verde ml-1">{s.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="font-manrope text-[#525252] text-xs">No scroll data yet — data will appear after visitors browse the site.</p>
            )}
          </div>

          {/* Recent Inquiries */}
          <div>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] mb-2">Recent Inquiries</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {stats.recent_submissions?.map((s) => (
                <div key={s.id} className="bg-ink-800 border border-ink-600 rounded-sm p-3">
                  <p className="font-manrope text-xs text-[#E5E5E5]">{s.first_name} {s.last_name}</p>
                  <p className="font-manrope text-[10px] text-[#525252]">{s.email} — {s.body_area}</p>
                </div>
              ))}
              {(!stats.recent_submissions || stats.recent_submissions.length === 0) && (
                <p className="font-manrope text-[#525252] text-xs text-center py-2">No inquiries yet.</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex justify-center py-8">
          <Loader2 size={24} className="animate-spin text-verde" />
        </div>
      )}
    </div>
  );
}
